/**
 * Data formatting utilities for analytics
 * Consistent number, date, and currency formatting (locale-aware via active i18n language)
 */
import i18n from '../../../shared/i18n/i18n.js';
import { resolveAdminLocaleTag } from '../../../shared/hooks/useAdminLocaleTag.js';

function getLocaleTag(): string {
  return resolveAdminLocaleTag(i18n.resolvedLanguage ?? i18n.language);
}

/**
 * Format number with thousand separators
 */
export function formatNumber(value: number, localeTag?: string): string {
  return new Intl.NumberFormat(localeTag ?? getLocaleTag(), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format currency (CZK)
 */
export function formatCurrency(value: number, localeTag?: string): string {
  return new Intl.NumberFormat(localeTag ?? getLocaleTag(), {
    style: 'currency',
    currency: 'CZK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format percentage
 */
export function formatPercentage(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format date for display
 */
export function formatDate(date: string | Date, localeTag?: string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(localeTag ?? getLocaleTag(), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/**
 * Format date and time for display
 */
export function formatDateTime(date: string | Date, localeTag?: string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(localeTag ?? getLocaleTag(), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/**
 * Format date range
 */
export function formatDateRange(from: string | Date, to: string | Date, localeTag?: string): string {
  const tag = localeTag ?? getLocaleTag();
  return `${formatDate(from, tag)} - ${formatDate(to, tag)}`;
}

/**
 * Format compact number (e.g., 1.2K, 3.4M)
 */
export function formatCompactNumber(value: number, localeTag?: string): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return formatNumber(value, localeTag);
}
